import React from 'react';
import {Swiper, SwiperSlide} from 'swiper/react';
import {Autoplay} from 'swiper';
import type {Swiper as SwiperClass} from 'swiper/types';

//components
import HeroBanner from 'components/modules/HeroBanner';

//types
import type {SanityModuleHeroBanner} from 'domains/modules';

//styles
import 'swiper/swiper.min.css';

interface Props {
  modules: SanityModuleHeroBanner[];
}

/**
 * Consecutive `module.hero_banner` entries on the homepage render as one
 * auto-playing carousel. Fully content-driven: editors add/remove hero
 * banner modules in Sanity Studio — one banner renders exactly as before
 * (see the grouping in routes/($lang)/index.tsx), two or more become
 * rotating slides. Each slide reuses <HeroBanner/> so per-locale images
 * and internal/external link behavior stay identical.
 */
export default function HeroBannerCarousel({modules}: Props) {
  const swiperRef = React.useRef<SwiperClass>();
  const [activeIndex, setActiveIndex] = React.useState(0);

  // Lock the carousel to a single fixed height for every slide by giving the
  // track a constant aspect-ratio box (derived from the banners' authored
  // dimensions — they are all made to the same ratio). Each banner then fills
  // that box with object-cover. This is what finally stops the top-crop and
  // the load-time height jump that made the banner need a scroll to reveal:
  // the height is fixed from the first paint, identical across slides, and
  // never recalculated. (autoHeight, which measured each slide as its image
  // loaded, is intentionally gone.)
  const firstRef = modules[0]?.banner_image?.asset?._ref;
  const dims = firstRef?.match(/-(\d+)x(\d+)-\w+$/);
  const aspectRatio = dims ? `${dims[1]} / ${dims[2]}` : '2000 / 917';

  return (
    <section className="module--hero-banner-carousel relative">
      <div className="relative w-full overflow-hidden" style={{aspectRatio}}>
        <Swiper
          modules={[Autoplay]}
          autoplay={{
            delay: 5000,
            disableOnInteraction: false,
            pauseOnMouseEnter: true,
          }}
          loop
          slidesPerView={1}
          // Fill the aspect-ratio box by absolute inset-0 rather than
          // height:100%. iOS Safari does NOT resolve a child's percentage
          // height against a parent sized only by aspect-ratio, so h-full
          // there collapsed the Swiper and object-cover cropped the top;
          // absolute inset-0 gives a definite height Safari honours.
          className="!absolute inset-0 h-full w-full"
          onBeforeInit={(swiper) => {
            swiperRef.current = swiper;
          }}
          onSlideChange={(swiper) => setActiveIndex(swiper.realIndex)}
        >
          {modules.map((module, i) => (
            <SwiperSlide key={module._key} className="h-full">
              <HeroBanner module={module} fill priority={i === 0} />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
      <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 gap-2">
        {modules.map((module, i) => (
          <button
            key={module._key}
            type="button"
            aria-label={`Banner ${i + 1}`}
            onClick={() => swiperRef.current?.slideToLoop(i)}
            className={
              'h-2.5 w-2.5 rounded-full border border-white/60 transition-colors ' +
              (i === activeIndex ? 'bg-hk-orange' : 'bg-white/70')
            }
          />
        ))}
      </div>
    </section>
  );
}
